<template>
  <div class="w-screen h-fill-available grid">
    <div ref="mapRef" class="col-start-1 row-start-1"></div>
    <h1 class="hidden">{{ blok.seo?.title || 'Map' }}</h1>
    <ElementMapIntro
      v-if="blok.intro?.length"
      :blok="blok.intro[0]"
      class="absolute bottom-0 left-0 pointer-events-none z-map-ui opacity-0 transition-opacity duration-300"
      :class="{
        'tl:opacity-100': $route.name === 'MapOverview',
      }"
    />
    <transition name="fade">
      <div
        v-if="!loadedMap"
        class="col-start-1 row-start-1 z-map-above-ui bg-[--section-bg-color] flex items-center justify-center clamp-pt-20-40"
      >
        <ElementMapLoading
          :texts="blok.loadingTexts"
          :media="blok.loadingMedia"
        />
      </div>
    </transition>
    <div
      class="col-start-1 row-start-1 z-map-above-ui flex items-center justify-center pointer-events-none clamp-pt-35-50 page-px map-ui-pb"
    >
      <transition name="fade-up">
        <ElementErrorNoResults
          v-show="noResults"
          :blok="blok.errorNoResults[0]"
          @clear="doClearSearchAndFilters"
          class="w-full"
        />
      </transition>
    </div>
    <div
      class="col-start-1 row-start-1 z-map-ui flex items-end justify-end clamp-gap-2-4 pointer-events-none clamp-pt-35-50 page-px map-ui-pb"
    >
      <ElementSearchBar
        class="pointer-events-auto w-full clamp-max-w-85-140"
        v-model="searchTerm"
        :searchPlaceholder="blok.searchPlaceholder"
        :submitText="searchCta"
        @search="doSearch"
        @clear="doClearSearch"
      />
      <ElementMapFilter
        ref="mapFilterRef"
        filterName="map-character"
        :markerColor="markerColor"
        :activeFilters="activeFilters"
        @update:activeFilters="activeFilters = $event"
        class="pointer-events-auto shrink-0"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
// utils
import { useRouter } from 'vue-router';
import { useWindowSize } from '@vueuse/core';
import mapboxgl from 'mapbox-gl';
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
import ElementMapFilter from './ElementMapFilter.vue';
type ElementMapFilterComponent = typeof ElementMapFilter;

// data
import useMapGeojson, {
  LocationGeoJsonProperties,
} from '@/composables/useMapGeojson';
import useMapFilter from '@/composables/useMapFilter';
import useMapPaddingAndCenter from '@/composables/useMapPaddingAndCenter';
import useMapLayers from '@/composables/useMapLayers';
import { Fa6SolidIcon, TemplateMapOverviewStoryblok } from '@/types';
import { getBreakpoint } from '@/../uno-preset/preset-theme';

const props = defineProps<{
  blok: TemplateMapOverviewStoryblok;
  theme: {
    bg: string;
    text: string;
  };
  markerColor: {
    bg: string;
    text: string;
  };
}>();

// composable
const router = useRouter();
const { width: windowWidth } = useWindowSize();
const {
  state,
  geoJson,
  stories: locationStories,
  fetchAllLocations,
} = useMapGeojson();
const {
  setupGeoJsonSource,
  updateGeoJsonSource,
  setupMarkerIconImages,
  setupMarkerClusters,
  setupMarkers,
} = useMapLayers();

const { geoJsonFiltered, activeFilters } = useMapFilter(
  'map-character',
  geoJson,
);

// variables
const fallbackIcon: Fa6SolidIcon = 'shoe-prints';
const mobileBreakpoint = getBreakpoint('tl');

// refs
const mapRef = ref<HTMLElement | null>(null);
const mapFilterRef = ref<ElementMapFilterComponent | null>(null);
const map = ref<mapboxgl.Map | null>(null);
const loadedMap = ref(false);
const geoJsonAdded = ref(false);
const lastSearchedTerm = ref('');
const searchTerm = ref('');

// SEARCH
const searchCta = computed(() => {
  if (windowWidth.value < mobileBreakpoint) return '';
  return props.blok.searchCta || 'Search';
});
const doSearch = () => {
  lastSearchedTerm.value = searchTerm.value;
  fetchAllLocations({ searchTerm: searchTerm.value });
};
const doClearSearch = () => {
  searchTerm.value = '';
  lastSearchedTerm.value = '';
  fetchAllLocations({ searchTerm: '' });
};
const doClearSearchAndFilters = () => {
  doClearSearch();
  mapFilterRef.value?.clearFilters();
};
const noResults = computed(() => {
  return (
    state.value === 'NO_RESULTS' ||
    geoJsonFiltered.value?.data.features.length === 0
  );
});

// CURRENT LOCATION
const currentLocSlug = computed(
  () => router.currentRoute.value.params.location,
);
const currentLocData = computed(() => {
  if (!locationStories.value) return undefined;
  return locationStories.value.find((loc) => loc.slug === currentLocSlug.value);
});

watch(
  geoJsonFiltered,
  (newVal) => {
    if (newVal) {
      if (!map.value) return;
      updateGeoJsonSource(map.value, newVal);
    }
  },
  { immediate: true },
);

useMapPaddingAndCenter(map, router.currentRoute, currentLocData);
fetchAllLocations({ searchTerm: '' });

const loadingStartTime = ref(0);

// Initialize the map when the component is mounted
onMounted(() => {
  if (!mapRef.value) return;

  loadingStartTime.value = Date.now();

  map.value = new mapboxgl.Map({
    container: mapRef.value,
    style: import.meta.env.VITE_MAPBOX_STYLE_URL,
    center: [-74.5, 40], // starting position [lng, lat]
    zoom: 2, // starting zoom
    fadeDuration: 0,
  });

  // disable map rotation using right click + drag or touch rotation gesture
 
  // @ts-ignore
  map.value.dragRotate.disable();
  // @ts-ignore
  map.value.touchZoomRotate.disableRotation();
  

  // add zoom control buttons
  // map.value.addControl(new mapboxgl.NavigationControl(), 'bottom-left');

  map.value.on('load', () => {
    setupMapLayersAndEvents();

    // make sure the loading screen is shown for at least X seconds
    const loadingEndTime = Date.now();
    const loadingElapsedTime = loadingEndTime - loadingStartTime.value;
    const loadingMinTimeMsShown = 3000;
    if (loadingElapsedTime < loadingMinTimeMsShown) {
      setTimeout(() => {
        loadedMap.value = true;
      }, loadingMinTimeMsShown - loadingElapsedTime);
    } else {
      loadedMap.value = true;
    }
  });
});

onUnmounted(() => {
  map.value!.remove();
});

// Reactively watch the geoJson ref for changes and update the map accordingly
watch(
  geoJson,
  (newVal) => {
    if (newVal && map.value) setupMapLayersAndEvents();
  },
  { immediate: true },
);

// set up map layers and events once map AND and geoJson are ready
function setupMapLayersAndEvents() {
  if (!map.value || !geoJson.value) return;

  // ensure the map is fully loaded before adding sources and layers
  if (!map.value.isStyleLoaded())
    map.value.once('style.load', () => addGeoJsonLayers());
  else addGeoJsonLayers();
}

// Add GeoJSON layers to the map
function addGeoJsonLayers() {
  if (!map.value || !geoJson.value) return;
  if (geoJsonAdded.value) return;
  geoJsonAdded.value = true;

  setupGeoJsonSource(map.value, geoJson.value);

  // console.log('geoJson', geoJson.value.data.features);

  // Load icons into the mapbox
  let svgIcons = [
    fallbackIcon,
    // ...venueTypes.map((venueType) => venueType.dimensions.icon),
  ]
    .filter((iconName) => iconName) // remove undefined
    .filter((iconName, index, self) => self.indexOf(iconName) === index); // remove duplicates
  setupMarkerIconImages(map.value, svgIcons);

  setupMarkerClusters(map.value, props.markerColor);
  setupMarkers(map.value, props.markerColor, router);
}

// CENTER MARKERS AFTER SEARCH OR FILTER
let lastInteractionState = ref<'searched' | 'filtered' | 'cleared'>('cleared');
watch([lastSearchedTerm, activeFilters], ([newS, newF], [oldS, oldF]) => {
  // chamged search or filters
  lastInteractionState.value =
    newS !== oldS && newS !== ''
      ? 'searched'
      : newF !== oldF && newF.length !== 0
        ? 'filtered'
        : 'cleared';

  // console.log('CHANGED lastInteractionState', lastInteractionState.value);

  // close map location route if open
  if (router.currentRoute.value.name === 'MapLocation') {
    const mapPath = router.currentRoute.value.path
      .split('/')
      .slice(0, -1)
      .join('/');
    router.push({ path: mapPath });
  }
});

watch(
  () => geoJsonFiltered.value?.data.features,
  (newFeatures) => {
    nextTick(() => {
      // wait for the lastInteractionState to be updated by using nextTick
      if (lastInteractionState.value !== 'cleared' && newFeatures?.length)
        centerMarkers(newFeatures);
    });
  },
);

const centerMarkers = (
  features: GeoJSON.Feature<GeoJSON.Point, LocationGeoJsonProperties>[],
) => {
  if (!map.value) return;
  const bounds = new mapboxgl.LngLatBounds();
  features.forEach((loc) => {
    bounds.extend({
      lng: loc.geometry.coordinates[0],
      lat: loc.geometry.coordinates[1],
    });
  });
  map.value.fitBounds(bounds, { maxZoom: 12 });
};
</script>
